/**
 * Ambulatory / home EEG ops workbench (Epic D4 / Gap #18).
 */

import React from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import FeatureAccessAlert from "@shared/components/FeatureAccessAlert";
import usePhaseFeatureAccessGuard from "@shared/hooks/usePhaseFeatureAccessGuard";
import { usePortalWorkbenchSnapshot } from "../services/portalWorkbenchApi";

export default function AmbulatoryOpsPanel() {
  const {
    accessLoading,
    accessDenied,
    accessDetail,
    accessReason,
    refreshAccess,
  } = usePhaseFeatureAccessGuard({
    phase: 2,
    featureKey: "AMBULATORY_EEG",
    featureUnavailableDetail:
      "Ambulatory / home EEG is not enabled for this organization.",
    missingOrgDetail:
      "Authenticated organization context is required to access ambulatory monitoring.",
  });
  const { snapshot, loading, error, reload } = usePortalWorkbenchSnapshot();
  const ambulatory = snapshot?.ambulatory;

  if (accessDenied) {
    return (
      <FeatureAccessAlert
        reason={accessReason}
        detail={accessDetail || "Ambulatory monitoring is not available."}
        onRetry={() => void refreshAccess()}
      />
    );
  }

  if (accessLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Checking ambulatory entitlement...</p>
      </div>
    );
  }

  return (
    <Container fluid className="py-3" data-testid="ambulatory-ops-panel">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="h4 mb-0">Ambulatory monitoring operations</h2>
        <Button size="sm" variant="outline-primary" onClick={reload} disabled={loading}>
          Refresh
        </Button>
      </div>
      {error ? <div className="alert alert-danger">{error}</div> : null}
      <Row className="g-3">
        <Col lg={6}>
          <Card className="mb-3">
            <Card.Header>Ambulatory / home studies</Card.Header>
            <Table size="sm" className="mb-0" data-testid="ambulatory-diary-table">
              <thead>
                <tr>
                  <th>Study</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4}>Loading ambulatory studies...</td></tr>
                ) : (ambulatory?.studies.length ?? 0) === 0 ? (
                  <tr><td colSpan={4}>No ambulatory or home studies are currently tagged in the database.</td></tr>
                ) : (
                  ambulatory?.studies.map((study) => (
                    <tr key={study.eeg_study_id}>
                      <td>{study.eeg_study_id}</td>
                      <td><Badge bg={study.priority === "stat" ? "danger" : "secondary"}>{study.priority}</Badge></td>
                      <td>{study.status}</td>
                      <td><Button as={Link as any} size="sm" variant="outline-primary" to={`/eeg/${study.eeg_study_id}`}>Open</Button></td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card>

          <Card className="mb-3">
            <Card.Header>Operational coverage</Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between"><span>Studies</span><strong>{ambulatory?.studies.length ?? 0}</strong></div>
              <div className="d-flex justify-content-between"><span>Processing logs</span><strong>{ambulatory?.ingestLogs.length ?? 0}</strong></div>
              <div className="d-flex justify-content-between"><span>Complete</span><strong>{snapshot?.summary.processingCompletePercent ?? 0}%</strong></div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="mb-3">
            <Card.Header>Home ingest diagnostics</Card.Header>
            <Card.Body data-testid="ambulatory-ingest-diag">
              {(ambulatory?.ingestLogs.length ?? 0) === 0 ? (
                <span className="text-muted">No recent ingest processing logs.</span>
              ) : (
                <Table size="sm" className="mb-0">
                  <tbody>
                    {ambulatory?.ingestLogs.map((log) => (
                      <tr key={log.log_id}>
                        <td>{log.operation_type}</td>
                        <td><Badge bg={log.operation_status === "completed" ? "success" : "warning"}>{log.operation_status}</Badge></td>
                        <td className="small text-muted">{log.ageSeconds != null ? `${Math.round(log.ageSeconds / 60)}m ago` : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
          <Card>
            <Card.Header>Review handoff</Card.Header>
            <Card.Body className="small text-muted" data-testid="aset-checklist">
              Monitoring activity is derived from stored studies and processing evidence.
              Billing or clinical handoff exports should be generated from the signed report workflow.
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
